import React, { useCallback, useEffect, useRef, useState } from 'react';
import { combineClassNames } from 'app/functions';
import Tag from 'components/common/shared/tag';

const SCROLL_STEP = 180;

const getScrollMetrics = (track) => {
    const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
    // Works for LTR and common RTL variants (including Chromium negative scrollLeft).
    const position = Math.min(Math.abs(track.scrollLeft), maxScroll);

    return { maxScroll, position };
};

export const HotYoterGroupTags = ({
    groupTags = [],
    selectedGroupId,
    onSelect,
    onRemove,
    testID = 'hot-yoter-group-tags',
}) => {
    const trackRef = useRef(null);
    const [canScrollStart, setCanScrollStart] = useState(false);
    const [canScrollEnd, setCanScrollEnd] = useState(false);

    const updateScrollState = useCallback(() => {
        const track = trackRef.current;

        if (!track) {
            setCanScrollStart(false);
            setCanScrollEnd(false);
            return;
        }

        const { maxScroll, position } = getScrollMetrics(track);
        const hasOverflow = maxScroll > 2;

        setCanScrollStart(hasOverflow && position > 2);
        setCanScrollEnd(hasOverflow && position < maxScroll - 2);
    }, []);

    useEffect(() => {
        const track = trackRef.current;

        if (!track) {
            return undefined;
        }

        updateScrollState();

        const handleScroll = () => updateScrollState();
        track.addEventListener('scroll', handleScroll, { passive: true });

        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => updateScrollState())
            : null;

        resizeObserver?.observe(track);
        window.addEventListener('resize', updateScrollState);

        return () => {
            track.removeEventListener('scroll', handleScroll);
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateScrollState);
        };
    }, [groupTags, selectedGroupId, updateScrollState]);

    const scrollTowardEnd = () => {
        const track = trackRef.current;
        if (!track) {
            return;
        }

        const rtl = getComputedStyle(track).direction === 'rtl';
        track.scrollBy({
            left: rtl ? -SCROLL_STEP : SCROLL_STEP,
            behavior: 'smooth',
        });
    };

    const scrollTowardStart = () => {
        const track = trackRef.current;
        if (!track) {
            return;
        }

        const rtl = getComputedStyle(track).direction === 'rtl';
        track.scrollBy({
            left: rtl ? SCROLL_STEP : -SCROLL_STEP,
            behavior: 'smooth',
        });
    };

    const showArrows = canScrollStart || canScrollEnd;

    return (
        <div
            className={combineClassNames(
                'hot-yoter-group-tags',
                showArrows && 'hot-yoter-group-tags_has-arrows',
            )}
            testId={testID}
        >
            {showArrows ? (
                <button
                    type="button"
                    className={combineClassNames(
                        'hot-yoter-group-tags__arrow',
                        'hot-yoter-group-tags__arrow_start',
                        !canScrollStart && 'hot-yoter-group-tags__arrow_disabled',
                    )}
                    onClick={scrollTowardStart}
                    disabled={!canScrollStart}
                    aria-label="הקודם"
                    testId={`${testID}-prev`}
                />
            ) : null}

            <div className="hot-yoter-group-tags__track" ref={trackRef} dir="rtl">
                {groupTags.map((tag) => (
                    <Tag
                        key={tag.id}
                        data={tag}
                        selectable
                        removable
                        isSelected={String(selectedGroupId) === String(tag.id)}
                        onTagClick={onSelect}
                        onRemove={onRemove}
                    />
                ))}
            </div>

            {showArrows ? (
                <button
                    type="button"
                    className={combineClassNames(
                        'hot-yoter-group-tags__arrow',
                        'hot-yoter-group-tags__arrow_end',
                        !canScrollEnd && 'hot-yoter-group-tags__arrow_disabled',
                    )}
                    onClick={scrollTowardEnd}
                    disabled={!canScrollEnd}
                    aria-label="הבא"
                    testId={`${testID}-next`}
                />
            ) : null}
        </div>
    );
};
